import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { IconLoader2, IconSend } from "@tabler/icons-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils/cn";
import { createTicket } from "../api";
import { CATEGORY_VALUES, PRIORITY_VALUES } from "../constants";

type NewTicketForm = {
  subject: string;
  category: "billing" | "technical" | "account" | "feature_request" | "other";
  priority: "low" | "medium" | "high" | "urgent";
  description: string;
};

interface NewTicketModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewTicketModal({ open, onOpenChange }: NewTicketModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const schema = useMemo(
    () =>
      z.object({
        subject: z.string().min(4, t("support.modal.errSubject")),
        category: z.enum([
          "billing",
          "technical",
          "account",
          "feature_request",
          "other",
        ]),
        priority: z.enum(["low", "medium", "high", "urgent"]),
        description: z.string().min(10, t("support.modal.errDescription")),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewTicketForm>({
    resolver: zodResolver(schema),
    defaultValues: { category: "technical", priority: "medium" },
  });

  const createMutation = useMutation({
    mutationFn: createTicket,
    onSuccess: (ticket) => {
      toast.success(t("support.modal.created", { code: ticket.code }));
      reset();
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["agency-support-tickets"] });
    },
    onError: () => toast.error(t("support.modal.createError")),
  });

  const onSubmit = (data: NewTicketForm) => createMutation.mutate(data);

  const fieldCls = (hasError?: boolean) =>
    cn(
      "w-full rounded-xl border border-border bg-gray-50 px-4 py-2.5 text-sm outline-none transition-all focus:border-brand-500 focus:bg-white focus:ring-4 focus:ring-brand-500/10",
      hasError && "border-red-500 focus:border-red-500 focus:ring-red-500/10",
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t("support.modal.title")}</DialogTitle>
          <DialogDescription>{t("support.modal.desc")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
          <div className="space-y-2">
            <label htmlFor="subject" className="text-sm font-bold text-ink">
              {t("support.modal.subject")}
            </label>
            <input
              id="subject"
              type="text"
              placeholder={t("support.modal.subjectPlaceholder")}
              className={fieldCls(!!errors.subject)}
              {...register("subject")}
            />
            {errors.subject && (
              <p className="text-xs font-bold text-red-500">
                {errors.subject.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="category" className="text-sm font-bold text-ink">
                {t("support.modal.category")}
              </label>
              <select
                id="category"
                className={fieldCls()}
                {...register("category")}
              >
                {CATEGORY_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {t(`support.category.${value}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label htmlFor="priority" className="text-sm font-bold text-ink">
                {t("support.modal.priority")}
              </label>
              <select
                id="priority"
                className={fieldCls()}
                {...register("priority")}
              >
                {PRIORITY_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {t(`support.priority.${value}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="description" className="text-sm font-bold text-ink">
              {t("support.modal.description")}
            </label>
            <textarea
              id="description"
              rows={5}
              placeholder={t("support.modal.descriptionPlaceholder")}
              className={cn(fieldCls(!!errors.description), "resize-none")}
              {...register("description")}
            />
            {errors.description && (
              <p className="text-xs font-bold text-red-500">
                {errors.description.message}
              </p>
            )}
          </div>

          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-xl px-4 py-2 text-sm font-bold text-ink-muted hover:bg-gray-100 transition-colors"
            >
              {t("support.modal.cancel")}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className={cn(
                "inline-flex items-center gap-2 rounded-xl px-6 py-2 text-sm font-black uppercase tracking-wider text-white transition-all shadow-soft",
                "bg-brand-grad hover:scale-105 hover:shadow-glow active:scale-95 disabled:opacity-50",
              )}
            >
              {createMutation.isPending ? (
                <IconLoader2 size={18} className="animate-spin" />
              ) : (
                <IconSend size={18} />
              )}
              {t("support.modal.submit")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
